import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';
import { registerGuard } from './RouterGuard';
import { VendorManagementRoutes } from './VendorManagementRoutes';
import { ClientServicesRoutes } from './ClientServicesRoutes';

const routes: Array<RouteRecordRaw> = [
  {
    path: '/',
    name: 'Root',
    component: () => import('@/views/layouts/MainLayoutView.vue'),
    children: [
      { path: '/', redirect: '/home' },
      {
        path: '/:pathMatch(.*)*',
        name: 'PageNotFound',
        component: () => import('@/views/PageNotFound.vue'),
      },
      {
        path: '/home',
        name: 'Home',
        component: () => import('@/views/HomeView.vue'),
        meta: { requiresAuth: true },
      },
      {
        path: '/administration',
        name: 'Administration',
        component: () => import('@/views/administration/AdministrationView.vue'),
        children: [
          {
            path: 'users',
            name: 'Users',
            component: () => import('@/views/administration/components/UsersView.vue'),
          },
          {
            path: 'role-permissions',
            name: 'RolePermissions',
            component: () => import('@/views/administration/components/RolePermissionsView.vue'),
          },
        ],
        meta: { requiresAuth: true },
      },
      {
        path: 'member-search',
        name: 'MemberInsight',
        component: () => import('@/views/member-search/MemberSearchView.vue'),
        children: [
          {
            path: '',
            name: 'MemberSearch',
            // meta: { title: 'Member Search', breadcrumb: true },
            component: () => import('@/views/member-search/components/MemberSearch.vue'),
          },
          {
            path: 'member-detail/:newTechId',
            name: 'MemberDetail',
            // meta: { title: 'Member Search', breadcrumb: true },
            component: () => import('@/views/member-search/components/MemberDetail.vue'),
          },
        ],
      },
      {
        path: '/accounts',
        name: 'Accounts',
        component: () => import('@/views/accounts/AccountsView.vue'),
        children: [
          { path: '', redirect: '/accounts/all', meta: { requiresAuth: true } },
          {
            path: 'all',
            name: 'AllAccounts',
            component: () => import('@/views/accounts/components/AllAccounts.vue'),
          },
          {
            path: 'my-accounts',
            name: 'MyAccounts',
            component: () => import('@/views/accounts/components/MyAccounts.vue'),
          },
          {
            path: '/accounts/:accountId',
            name: 'AccountDetails',
            props: (route) => ({
              accountId: Number.parseInt(route.params.accountId.toString()),
            }),
            component: () => import('@/views/accounts/components/AccountDetails.vue'),
            children: [
              {
                path: 'overview',
                name: 'AccountOverview',
                meta: { title: 'Overview', breadcrumb: true },
                component: () => import('@/views/accounts/components/account-overview/AccountOverview.vue'),
              },
              {
                path: 'implementation',
                name: 'ImplementationTracker',
                meta: { title: 'Implementation', breadcrumb: true },
                component: () => import('@/views/accounts/components/implementation/ImplementationTrackerDetails.vue'),
              },
              {
                path: 'accumulators',
                name: 'AccountAccumulators',
                meta: { title: 'Accumulators', breadcrumb: true },
                component: () => import('@/views/accounts/components/plan-design/AccumulatorConfigurationTabView.vue'),
              },
              {
                path: 'plan-design',
                name: 'AccountPlanDesign',
                meta: { title: 'Plan Design', breadcrumb: true },
                component: () => import('@/views/accounts/components/plan-design/PlanDesign.vue'),
              },
              {
                path: 'inheritance',
                name: 'AccountAdjudication',
                meta: { title: 'Inheritance', breadcrumb: true },
                component: () => import('@/views/accounts/components/adjudication/AccountAdjudication.vue'),
              },
              {
                path: 'plan-design/:planId',
                name: 'PlanDesignDetails',
                meta: {
                  title: 'Plan Overview',
                  breadcrumb: true,
                  showPlanName: true,
                },
                component: () => import('@/views/accounts/components/plan-design/PlanDesignDetails.vue'),
              },
              {
                path: 'plan-design/new',
                name: 'NewPlanDesign',
                meta: { title: 'Create New Plan', breadcrumb: true },
                component: () => import('@/views/accounts/components/plan-design/CreateNewPlanDesign.vue'),
              },
              {
                path: 'contacts',
                name: 'AccountContacts',
                meta: { title: 'Contacts', breadcrumb: true },
                component: () => import('@/views/accounts/components/Contacts/AllContacts.vue'),
              },
              {
                path: 'microsite',
                name: 'AccountMicrosite',
                meta: { title: 'Microsite', breadcrumb: true },
                component: () => import('@/views/accounts/components/microsite/MicrositeCustomization.vue'),
              },
              {
                path: 'programs',
                name: 'AccountPrograms',
                meta: { title: 'Programs', breadcrumb: true },
                component: () => import('@/views/accounts/components/programs/AccountPrograms.vue'),
              },
            ],
          },
        ],
        meta: { requiresAuth: true },
      },
      {
        path: 'clinical-old',
        name: 'Clinical',
        component: () => import('@/views/clinical/ClinicalView.vue'),
      },
      {
        path: '/clinical',
        children: [
          {
            path: '/clinical',
            name: 'Clinical',
            component: () => import('@/views/clinical/ClinicalViewNew.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'overview',
            name: 'ClinicalOverview',
            meta: { title: 'Claim Edit Rules', breadcrumb: true },
            component: () => import('@/views/clinical/components/OverviewTab.vue'),
          },
          {
            path: 'overview/:claimEditId',
            name: 'RuleDetails',
            meta: { title: 'Claim Edit Rules', breadcrumb: true },
            component: () => import('@/views/clinical/components/rule-details/RuleDetails.vue'),
          },
          {
            path: 'template-management',
            name: 'ClaimTemplateManagement',
            component: () => import('@/views/clinical/components/ClaimTemplateManagementTab.vue'),
            meta: { title: 'Claim Template Management', breadcrumb: true },
          },
          {
            path: 'template-management/:claimEditTemplateId',
            name: 'ClaimTemplateDetail',
            component: () => import('@/views/clinical/components/ClaimTemplateDetails.vue'),
            meta: { title: 'Claim Template Management', breadcrumb: true },
          },
          {
            path: 'list-management',
            name: 'ListManagement',
            meta: { title: 'List Mangement', breadcrumb: true },
            component: () => import('@/views/clinical/components/ListManagementTab.vue'),
          },
          {
            path: 'list-management/:listId/:listType',
            name: 'ListDetail',
            meta: { title: 'List Management', breadcrumb: true },
            component: () => import('@/views/clinical/components/ListDetailView.vue'),
          },
          {
            path: 'create-new-rule',
            alias: 'create-new-rule/:claimEditTemplateId',
            name: 'CreateNewRule',
            meta: { title: 'Claim Edit Rules', breadcrumb: true, subTitle: 'Create New Rule' },
            component: () => import('@/views/clinical/components/CreateNewRule.vue'),
          },
          {
            path: 'overrides',
            name: 'Overrides',
            component: () => import('@/views/clinical/components/OverridesTab.vue'),
            meta: { title: 'Overrides', breadcrumb: true },
          },
          {
            path: 'override-detail/:overrideId',
            name: 'OverrideDetail',
            meta: { title: 'Overrides', breadcrumb: true },
            component: () => import('@/views/clinical/components/create-override/override-details/OverrideDetails.vue'),
          },
          {
            path: 'create-new-override',
            alias: 'create-new-override/:overrideId',
            name: 'CreateNewOverrides',
            meta: { title: 'Overrides', breadcrumb: true, subTitle: 'Create New Overrides' },
            component: () => import('@/views/clinical/components/CreateNewOverrides.vue'),
          },
          {
            path: 'formulary-overrides-old',
            name: 'FormularyOverrides',
            component: () => import('@/views/clinical/components/FormularyOverridesTab.vue'),
            meta: { title: 'Formulary Overrides', breadcrumb: true },
          },
          {
            path: 'formulary-overrides',
            name: 'FormularyOverrides',
            component: () => import('@/views/clinical/FormularyOverridesListView.vue'),
            meta: { title: 'Formulary Overrides', breadcrumb: true },
          },
          {
            path: 'override-detailed-summary',
            name: 'override-detailed-summary',
            alias: 'formulary-overrides/:tierName/override-detailed-summary/:overrideId',
            meta: { title: 'Formulary Overrides', breadcrumb: true },
            component: () => import('@/views/clinical/components/FormularyOverrideSummary.vue'),
          },
          {
            path: 'formulary-overrides/:tierName',
            name: 'TierOverrides',
            meta: { title: 'Formulary Overrides', breadcrumb: true },
            component: () => import('@/views/clinical/components/TierOverrides.vue'),
          },
          {
            path: 'formulary-overrides/create-tier-override',
            alias: '/create-tier-override',
            name: 'CreateTierOverride',
            meta: { title: 'Formulary Overrides', breadcrumb: true, subTitle: 'New Formulary Override' },
            component: () => import('@/views/clinical/components/CreateTierOverride.vue'),
          },
          {
            path: 'list-management/new-custom-list',
            alias: '/new-custom-list',
            name: 'NewCustomList',
            meta: { title: 'List Management', breadcrumb: true, subTitle: 'New Custom List' },
            component: () => import('@/views/clinical/components/NewCustomList.vue'),
          },
        ],
        meta: { requiresAuth: true },
      },
      {
        ...VendorManagementRoutes,
      },
      {
        ...ClientServicesRoutes,
      },
      //{
      //  path: '/druglookup',
      //  name: 'Drugs',
      //  component: () => import('@/views/lookup/drugs/DrugsView.vue'),
      //  meta: { requiresAuth: true },
      //},
      //{
      //  path: '/druglookup/results',
      //  props: { searchBy: '', params: '', 'date-service': '' },
      //  name: 'DrugSearch',
      //  component: () => import('@/views/lookup/drugs/DrugsView.vue'),
      //  meta: { requiresAuth: true },
      //},
      {
        path: '/lookup',
        name: 'Lookup',
        component: () => import('@/views/lookup/LookupView.vue'),
        children: [
          {
            path: 'drugs',
            alias: '/lookup',
            name: 'DrugLookup',
            component: () => import('@/views/lookup/drugs/DrugsView.vue'),
            meta: { requiresAuth: true },
          },
          {
            path: 'claims',
            name: 'ClaimLookup',
            component: () => import('@/views/lookup/claims/ClaimLookupView.vue'),
            meta: { requiresAuth: true },
          },
        ],
        meta: { requiresAuth: true },
      },
      {
        path: '/testing',
        name: 'Testing',
        component: () => import('@/views/testing/TestingView.vue'),
        meta: { requiresAuth: true, title: 'Testing' },
        children: [
          {
            path: '',
            name: 'TestingHub',
            component: () => import('@/views/testing/TestingHubView.vue'),
            meta: { title: 'Testing Hub' },
          },
          {
            path: 'test-builder',
            name: 'TestBuilder',
            component: () => import('@/views/testing/TestBuilderView.vue'),
            meta: { title: 'Test Builder' },
          },
          {
            path: 'test-queue',
            name: 'TestQueue',
            component: () => import('@/views/testing/TestQueueView.vue'),
            meta: { title: 'Test Queue' },
          },
          {
            path: 'test-queue/:jobId',
            name: 'JobDetail',
            component: () => import('@/views/testing/JobDetailView.vue'),
            meta: { title: 'Job Detail' },
          },
        ],
      },
      {
        path: '/pharmacy-network',
        name: 'Pharmacy',
        component: () => import('@/views/pharmacy/PharmacyView.vue'),
      },
      {
        path: '/pharmacy-network/network',
        name: 'Network',
        component: () => import('@/views/pharmacy/components/network/NetworkView.vue'),
      },
      {
        path: '/pharmacy-network/network/networkView',
        name: 'NetworkView',
        component: () => import('@/views/pharmacy/components/network/OpenNetworkView.vue'),
      },
      {
        path: '/pharmacy-network/pharmacies',
        name: 'Pharmacies',
        component: () => import('@/views/pharmacy/components/pharmacies/PharmaciesView.vue'),
      },
      {
        path: '/pharmacy-network/pharmacies/customView/:pharmacyId',
        name: 'PharmacyCustomProfile',
        component: () => import('@/views/pharmacy/components/pharmacies/PharmacyDetails.vue'),
      },
      {
        path: '/pharmacy-network/pharmacies/nonCustomView/:pharmacyId/:isPharmacy',
        name: 'PharmacyNonCustomProfile',
        component: () => import('@/views/pharmacy/components/pharmacies/PharmacyProfile.vue'),
      },
      {
        path: '/pharmacy-network/pharmacies/pharmacy-grouping',
        name: 'PharmacyGrouping',
        component: () => import('@/views/pharmacy/components/pharmacies/NewPharmacyGrouping.vue'),
      },
      {
        path: '/pharmacy-network/discounts',
        name: 'PharmacyDiscounts',
        component: () => import('@/views/pharmacy/components/discount/DrugPricingManagement.vue'),
      },
      {
        path: '/pharmacy-network/discounts/new-pricing-list',
        name: 'CreateNewPricingList',
        component: () => import('@/views/pharmacy/components/discount/NewPricingList.vue'),
      },
      {
        path: '/pharmacy-network/discounts/:listSource/:customDiscountId',
        name: 'CustomDiscountDetails',
        component: () => import('@/views/pharmacy/components/discount/CustomDiscountDetails.vue'),
      },
      {
        path: '/pharmacy-network/pricing-list-details/:status/:pricingListId',
        name: 'PricingListDetails',
        component: () => import('@/views/pharmacy/components/discount/PricingListDetails.vue'),
      },
      {
        path: '/pharmacy-network/fee-schedules',
        name: 'FeeSchedules',
        component: () => import('@/views/pharmacy/components/feeschedule/AllFeeSchedules.vue'),
      },
      {
        path: '/pharmacy-network/fee-schedules/create-fee-schedule',
        name: 'NewFeeSchedule',
        component: () => import('@/views/pharmacy/components/feeschedule/FeeScheduleBuilderMaster.vue'),
      },
      {
        path: '/pharmacy-network/tax-rates',
        name: 'TaxRates',
        component: () => import('@/views/pharmacy/components/taxRate/TaxRateList.vue'),
      },
      {
        path: '/pharmacy-network/fee-schedules/fee-schedule-details/:feeScheduleId',
        name: 'ViewFeeSchedule',
        component: () => import('@/views/pharmacy/components/feeschedule/FeeScheduleDetails.vue'),
      },
      {
        path: '/pharmacy-network/fee-schedules/custom-fee-schedule-details/:feeScheduleId',
        name: 'ViewCustomFeeSchedule',
        component: () => import('@/views/pharmacy/components/feeschedule/FeeScheduleDetails.vue'),
      },
      {
        path: '/pharmacy-network/remits',
        name: 'Remits',
        component: () => import('@/views/pharmacy/components/remits/RemitsList.vue'),
      },

      {
        path: '/pharmacy-network/remits/:remitAndRecId',
        name: 'RemitsDetailView',
        component: () => import('@/views/pharmacy/components/remits/RemitsDetailView.vue'),
      },
      {
        path: '/pharmacy-network/remits/create-new-remit',
        name: 'CreateNewRemit',
        component: () => import('@/views/pharmacy/components/remits/CreateNewRemit.vue'),
        meta: {
          title: 'New Remit Center',
          breadcrumb: true,
        },
      },

      {
        path: '/pharmacy-network/payment-centers',
        name: 'PaymentCenters',
        component: () => import('@/views/pharmacy/components/payments/PaymentCenterList.vue'),
      },
      {
        path: '/pharmacy-network/payment-centers/:pCenterId',
        name: 'PaymentDetailView',
        component: () => import('@/views/pharmacy/components/payments/PaymentDetailView.vue'),
        children: [
          {
            path: 'payment-overview',
            name: 'PaymentOverview',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/payments/components/payment-center/PaymentOverview.vue'),
          },
          {
            path: 'payment-invoices',
            name: 'PaymentInvoices',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/payments/components/payment-center/PaymentInvoices.vue'),
          },
          {
            path: 'payments',
            name: 'PaymentsList',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/payments/components/payment-center/PaymentsList.vue'),
          },
        ],
      },
      {
        path: '/pharmacy-network/payments/create-new-payment',
        name: 'CreateNewPayment',
        meta: { title: 'New Payment Center', breadcrumb: true },
        component: () => import('@/views/pharmacy/components/payments/CreateNewPayment.vue'),
      },
      {
        path: '/pharmacy-network/payment-lookup',
        name: 'PaymentLookup',
        component: () => import('@/views/pharmacy/components/payments/PaymentLookupView.vue'),
      },
      {
        path: '/pharmacy-network/vendors',
        name: 'VendorDetailsView',
        component: () => import('@/views/pharmacy/components/vendors/VendorList.vue'),
      },
      {
        path: '/pharmacy-network/custom-pricing',
        name: 'CustomPricing',
        component: () => import('@/views/pharmacy/customPricing/CustomPricingList.vue'),
      },
      {
        path: '/pharmacy-network/vendor/:vendorId',
        name: 'VendorDetails',
        component: () => import('@/views/pharmacy/components/vendors/VendorDetails.vue'),
        children: [
          {
            path: 'vendor-overview',
            name: 'VendorOverview',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/vendors/VendorOverview.vue'),
          },
          {
            path: 'vendor-invoices',
            name: 'VendorInvoice',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/vendors/VendorInvoice.vue'),
          },
          {
            path: 'vendor-payment-list',
            name: 'VendorPaymentList',
            meta: { title: '', breadcrumb: true },
            component: () => import('@/views/pharmacy/components/vendors/VendorPaymentList.vue'),
          },
        ],
      },
      {
        path: '/pharmacy-network/mac-lists',
        name: 'MacLists',
        component: () => import('@/views/pharmacy/components/macLists/MacListsView.vue'),
      },
      {
        path: '/pharmacy-network/mac-appeals',
        name: 'MacAppeals',
        component: () => import('@/views/pharmacy/components/macAppeals/MacAppealsView.vue'),
      },
      {
        path: '/pharmacy-network/mac-view-details/:macAppealId',
        name: 'MacViewDetails',
        component: () => import('@/views/pharmacy/components/macAppeals/MacViewDetails.vue'),
      },
    ],
    meta: { title: 'Home', requiresAuth: true },
  },
  {
    path: '/',
    name: 'EmptyLayout',
    component: () => import('@/views/layouts/EmptyLayoutView.vue'),
    children: [
      {
        path: 'login',
        name: 'Login',
        component: () => import('@/views/LoginView.vue'),
      },
    ],
  },
  {
    path: '/callback',
    name: 'callback',
    component: () => import('@/views/CallbackView.vue'),
  },
  {
    path: '/AccessDenied',
    name: 'AccessDenied',
    component: () => import('@/views/AccessDeniedView.vue'),
  },
  // {
  //   path: '/:pathMatch(.*)*',
  //   name: 'PageNotFound',
  //   component: () => import('@/views/PageNotFound.vue'),
  // },
];

export const router = createRouter({
  scrollBehavior(to, from, savedPosition) {
    const scrollContainer = document.getElementById('body-container');
    // Only react if the actual route path (not query) changed
    if (to.path !== from.path) {
      if (savedPosition) {
        scrollContainer?.scrollTo({
          ...savedPosition,
          behavior: 'smooth',
        });
      } else {
        scrollContainer?.scrollTo({
          top: 0,
          behavior: 'smooth',
        });
      }
    }
    // Prevents Vue Router default scrolling
    return false;
  },
  history: createWebHistory(import.meta.env.BASE_URL),
  routes,
});

registerGuard(router);

export default router;
